// src/catalog/components/JoinProjectWithCode.jsx

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/authContext";
import {
  ApplicationButton,
  ApplicationField,
  ApplicationSectionHeader,
  ApplicationStateRegion
} from "../../../components/application";

const apiUrl =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

function getStoredAuthToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken")
  );
}

export default function JoinProjectWithCode({
  accessCodeInputId = "accessCode",
  headingLevel = 2,
  sectionId,
  title = "Join a project",
  titleId = "dashboard-project-access-title"
} = {}) {
  const navigate = useNavigate();
  const auth = useAuth();

  const [accessCode, setAccessCode] = useState("");
  const [participantIdentifier, setParticipantIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();

    if (!accessCode.trim()) {
      return;
    }

    setLoading(true);
    setMessage("");
    setError("");

    try {
      const token =
        auth.user?.token ||
        getStoredAuthToken();

      if (!token) {
        throw new Error("You need to log in before joining a project.");
      }

      const response = await fetch(
        `${apiUrl}/projects/join-with-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            accessCode: accessCode.trim(),
            participantIdentifier:
              participantIdentifier.trim() || undefined
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
          data.error ||
          "Could not join project with this access code."
        );
      }

      const projectId =
        data.project?._id ||
        data.project?.id ||
        data.projectId;

      if (projectId) {
        localStorage.setItem(
          "just_joined_project",
          projectId
        );
      }

      setAccessCode("");
      setParticipantIdentifier("");

      setMessage(
        data.message ||
        "Project joined successfully."
      );

      if (data.participationRoute) {
        navigate(data.participationRoute);
        return;
      }

      if (data.nextRoute) {
        navigate(data.nextRoute);
        return;
      }

      if (data.project?.slug) {
        navigate(`/app/project/${data.project.slug}`);
      }

    } catch (err) {
      setError(
        err.message ||
        "Could not join project."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <section
      id={sectionId}
      aria-labelledby={titleId}
      className="av-dashboard-project-access"
    >
      <ApplicationSectionHeader
        headingLevel={headingLevel}
        title={title}
        titleId={titleId}
        variant="compact"
      />

      <div className="av-dashboard-project-access__body">
        <p>
          Access codes are provided by course, research, or institutional
          project organizers.
        </p>

        <form
          aria-busy={loading}
          className="av-dashboard-entry-form av-dashboard-project-access__form"
          onSubmit={handleSubmit}
        >
          <ApplicationField
            id={accessCodeInputId}
            label="Access code"
            value={accessCode}
            onChange={(event) =>
              setAccessCode(event.target.value.toUpperCase())
            }
            placeholder="Access code"
            autoComplete="off"
            required
          />

          <ApplicationField
            id="participantIdentifier"
            label="Participant ID"
            value={participantIdentifier}
            onChange={(event) =>
              setParticipantIdentifier(event.target.value)
            }
            placeholder="Enter the ID provided by your project organizer, or leave blank if none was provided."
            autoComplete="off"
          />

          <ApplicationButton
            className="av-dashboard-entry-form__submit"
            type="submit"
            disabled={loading}
          >
            {loading ? "Joining..." : "Join project"}
          </ApplicationButton>
        </form>

        {message && (
          <ApplicationStateRegion
            message={message}
            status="success"
          />
        )}

        {error && (
          <ApplicationStateRegion
            message={error}
            status="error"
          />
        )}
      </div>
    </section>
  );
}
